/**
 * The one modal in this codebase.
 *
 * Seven files imported Modal/ModalHeader/ModalBody from reactstrap. reactstrap
 * renders Bootstrap markup and nothing more: the overlay, the centring and the
 * panel all come from Bootstrap's CSS, which was never a dependency here and was
 * never imported. Every one of those seven modals has therefore rendered as
 * unstyled inline markup since the day it was written.
 *
 * This is built on MUI Dialog, which is already a core dependency and ships its
 * own styles, so it works. The prop names deliberately match what reactstrap
 * took (`isOpen`, `toggle`) so the seven call sites keep the state handling they
 * already have; adapting is cheaper and safer than rewriting seven components,
 * and leaves one place to change if the modal ever needs to behave differently.
 */
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const panelSx = {
  background: 'linear-gradient(135deg, rgba(26,26,46,0.98) 0%, rgba(15,15,26,1) 100%)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  color: '#fff',
};

export default function AppModal({
  isOpen = false,
  toggle,
  title,
  children,
  maxWidth = 'sm',
  ...rest
}) {
  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={toggle}
      maxWidth={maxWidth}
      fullWidth
      data-testid="app-modal"
      PaperProps={{ sx: panelSx }}
      {...rest}
    >
      {title ? (
        <DialogTitle
          sx={{ pr: 6, color: '#fff' }}
          data-testid="app-modal-title"
        >
          {title}
          {toggle ? (
            <IconButton
              aria-label="close"
              onClick={toggle}
              data-testid="app-modal-close"
              sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.6)' }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
      ) : null}
      <DialogContent data-testid="app-modal-body">{children}</DialogContent>
    </Dialog>
  );
}
